import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtDayShort } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Trophy } from "lucide-react";
import { TechniqueDetails } from "@/components/TechniqueDetails";
import { FixtureStats, StatValues, DerivedStats } from "@/components/FixtureStats";
import { Button } from "@/components/ui/button";
import { errorText } from "@/components/academy/HubUI";

const iso = (d) => new Date(d).toISOString().slice(0, 10);
const RESULT_COLOR = { win: "text-traffic-green", loss: "text-traffic-red", draw: "text-muted-foreground" };

export default function MatchTrends({ athleteId }) {
  const [d, setD] = useState(null);
  const [start, setStart] = useState(iso(new Date(Date.now() - 365 * 864e5)));
  const [end, setEnd] = useState(iso(new Date()));
  const [metric, setMetric] = useState("");
  const [error, setError] = useState(""), [viewId, setViewId] = useState(""), [editId, setEditId] = useState("");
  const active = d?.matches.find(m => m.fixture_id === viewId);

  const load = useCallback(() => {
    if (!athleteId) return;
    setError("");
    api.get(`/athletes/${athleteId}/match-trends?start=${start}&end=${end}`)
      .then((r) => { setD(r.data); const n = (r.data.fields || []).find((f) => f.type === "number" && r.data.matches.some(row=>typeof row.stats?.[f.key] === "number")); setMetric((m) => r.data.fields.some(f=>f.key===m) ? m : n?.key || ""); })
      .catch(e => { setD(null); setError(errorText(e)); });
  }, [athleteId, start, end]);
  useEffect(() => { load(); }, [load]);

  const numeric = (d?.fields || []).filter(f => f.type === "number" && (d?.matches || []).some(m => typeof m.stats?.[f.key] === "number"));
  const chart = useMemo(() => (d?.matches || []).map((m) => ({
    date: fmtDayShort(m.date),
    stat: typeof m.stats?.[metric] === "number" ? m.stats[metric] : null,
    load: m.load_7d,
    rest: m.rest ?? null,
    effort: m.effort ?? null,
  })), [d, metric]);

  if (!d) return <div data-testid="match-trends-status" className="p-4 sm:p-8 text-sm text-muted-foreground">{error ? <><p role="alert" data-testid="match-trends-error">{error}</p><Button data-testid="match-trends-retry" onClick={load}>Retry</Button><Button data-testid="match-trends-reset-range" variant="ghost" onClick={()=>{setStart(iso(new Date(Date.now()-365*864e5)));setEnd(iso(new Date()));}}>Reset date range</Button></> : "Loading match history…"}</div>;

  return (
    <div className="p-4 sm:p-8" data-testid="match-trends">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Trophy className="w-4 h-4" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold flex-1">Match stats · season</span>
        <input data-testid="mt-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="inp w-auto text-xs py-1.5" />
        <input data-testid="mt-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="inp w-auto text-xs py-1.5" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-6">
        {[["Matches", d.matches.length], ["With stats recorded", d.played],
          ["Sport", d.sport || "-"], ["Team", d.squad || "-"]].map(([k, v]) => (
          <div key={k} className="bg-background p-3" data-testid={`mt-kpi-${String(k).toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
            <div className="font-heading text-xl font-black tabular-nums truncate">{v}</div>
          </div>
        ))}
      </div>

      <p data-testid="match-stats-linked-note" className="text-sm text-muted-foreground mb-5">These are this athlete’s contributions, read directly from their team fixtures. Correcting the fixture updates this view—there is no second entry or separate copy.</p>
      {active && <section data-testid="athlete-match-detail" className="border border-border rounded-xl p-5 space-y-5 mb-6"><div className="flex justify-between gap-4"><h3 className="font-heading text-lg font-bold">{active.opponent || active.type} · {fmtDate(active.date)}</h3><Button data-testid="athlete-match-detail-close" variant="ghost" onClick={()=>setViewId("")}>Close</Button></div><p data-testid="athlete-match-provenance" className="text-xs text-muted-foreground">{active.sport} · {active.squad || "Team not recorded"} · {active.result || "Outcome not recorded"} {active.score} · Participation: {active.participation_status.replaceAll("_"," ")} · Shared fixture revision {active.result_revision}</p><h4 className="text-sm font-semibold">{d.name} · individual contribution</h4><StatValues fields={d.schemas[active.sport]?.fields} values={active.stats} prefix="athlete-match-recorded"/><DerivedStats schema={d.schemas[active.sport]} values={active.derived_stats} prefix="athlete-match"/>{d.schemas[active.sport]?.technical && <TechniqueDetails key={active.fixture_id} schema={d.schemas[active.sport].technical} value={active.techniques} savedRows={active.technique_breakdown} editable={false} prefix="athlete-techniques"/>}<details><summary data-testid="athlete-match-team-context" className="text-sm cursor-pointer underline">Team / fixture totals (context only—not credited to this player)</summary><div className="mt-4"><StatValues fields={d.schemas[active.sport]?.team_fields} values={active.team_stats} prefix="athlete-team-context"/></div></details>{active.can_open_team && <Button data-testid="athlete-open-source-fixture" variant="outline" onClick={()=>setEditId(active.fixture_id)}>Open source team scorecard</Button>}</section>}
      {editId && <FixtureStats fixtureId={editId} onClose={()=>setEditId("")} onSaved={load}/>}
      {d.matches.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="mt-empty">
          No matches in this window. Create a fixture under Matches &amp; Fixtures and record the result · the stats
          for {d.sport || "this sport"} will then build up here next to the athlete's load and recovery.
        </p>
      ) : (
        <>
          {numeric.length > 0 && (
            <div className="border border-border mb-6">
              <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
                <span className="text-xs tracking-[0.18em] uppercase font-bold">Output vs load &amp; recovery</span>
                <select data-testid="mt-metric" value={metric} onChange={(e) => setMetric(e.target.value)} className="inp w-auto text-xs py-1 ml-auto">
                  {[...new Set(numeric.map((f) => f.group || "Other"))].map((g) => <optgroup key={g} label={{ box: "Match stats", event: "Event stats", common: "General stats", Other: "Other stats" }[g] || g}>{numeric.filter((f) => (f.group || "Other") === g).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}</optgroup>)}
                </select>
              </div>
              <div className="h-64 p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chart} margin={{ top: 5, right: 8, bottom: 0, left: -18 }}>
                    <CartesianGrid stroke="#eee" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 0 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="l" type="monotone" dataKey="stat" name={numeric.find((f) => f.key === metric)?.label || metric} stroke="#D2378B" strokeWidth={2} dot connectNulls={false} />
                    <Line yAxisId="r" type="monotone" dataKey="load" name="Load 7d" stroke="#0a0a0a" strokeWidth={1.5} dot={false} />
                    <Line yAxisId="l" type="monotone" dataKey="rest" name="Rest+ that day" stroke="#16a34a" strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="px-4 pb-3 text-[11px] text-muted-foreground">
                Lines are plotted side by side so you can see them together. This is not a causal claim · a good
                match after a light week is not proof the light week caused it.
              </p>
            </div>
          )}

          <div className="scroll-x border border-border">
            <table className="w-full text-sm" data-testid="mt-table">
              <thead>
                <tr className="bg-surface border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-2 whitespace-nowrap">Date</th>
                  <th className="text-left p-2">Opponent</th>
                  <th className="text-left p-2">Result / scorecard</th>
                  {numeric.map((f) => <th key={f.key} className="text-left p-2 whitespace-nowrap">{f.label}</th>)}
                  <th className="text-left p-2 whitespace-nowrap">Load 7d</th>
                  <th className="text-left p-2 whitespace-nowrap">ACWR</th>
                  <th className="text-left p-2 whitespace-nowrap">Rest+</th>
                  <th className="text-left p-2 whitespace-nowrap">Effort+</th>
                </tr>
              </thead>
              <tbody>
                {d.matches.map((m) => (
                  <tr key={m.fixture_id} className="border-b border-border last:border-0" data-testid={`mt-row-${m.fixture_id}`}>
                    <td className="p-2 tabular-nums whitespace-nowrap">{fmtDate(m.date)}</td>
                    <td className="p-2">{m.opponent || m.type} {m.competition ? <span className="text-muted-foreground">· {m.competition}</span> : null}</td>
                    <td className={`p-2 ${RESULT_COLOR[m.result] || "text-muted-foreground"}`}><p>{m.result || "Not recorded"} {m.score}</p><button data-testid={`mt-view-${m.fixture_id}`} className="text-xs underline mt-2" onClick={()=>setViewId(m.fixture_id)}>View player stats</button></td>
                    {numeric.map((f) => (
                      <td key={f.key} data-testid={`mt-stat-${m.fixture_id}-${f.key}`} className="p-2 tabular-nums">
                        {m.stats?.[f.key] ?? <span className="text-muted-foreground">Not recorded</span>}
                      </td>
                    ))}
                    <td className="p-2 tabular-nums">{m.load_7d}</td>
                    <td className="p-2 tabular-nums">{m.acwr ?? "-"}</td>
                    <td className="p-2 tabular-nums">{m.rest ?? <span className="text-muted-foreground">-</span>}</td>
                    <td className="p-2 tabular-nums">{m.effort ?? <span className="text-muted-foreground">-</span>}</td>
                  </tr>
                ))}
                <tr className="bg-surface font-bold" data-testid="mt-totals">
                  <td className="p-2" colSpan={3}>Season total <span className="font-normal text-muted-foreground">(avg)</span></td>
                  {numeric.map((f) => (
                    <td key={f.key} className="p-2 tabular-nums">
                      {d.totals?.[f.key] ?? "-"}
                      {d.averages?.[f.key] != null && <span className="font-normal text-muted-foreground"> ({d.averages[f.key]})</span>}
                    </td>
                  ))}
                  <td className="p-2" colSpan={4}></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">{d.note}</p>
        </>
      )}
    </div>
  );
}
